import { Modal } from "@JohnLewisPartnership/jl-design-system/dist/components/modal/Modal";
import { useEffect } from "react";
import { useVariant } from "@~/contexts/VariantContext/VariantContext.tsx";
import { Product } from "@~/types/types.ts";
import { getAnalyticsData } from "@~/product-card-components/__shared__/QuickViewModal/utils.ts";
import { useExperienceString } from "@~/flags/flags";

declare global {
  // eslint-disable-next-line @typescript-eslint/no-namespace
  namespace React.JSX {
    export interface IntrinsicElements {
      "quick-view": {
        "data-testid"?: string;
        config?: string;
        "pim-product-id"?: string;
        "initial-variant-id"?: string;
        "analytics-additional-info"?: string;
        "experiences-raw-string"?: string;
      };
    }
  }
}

export type QuickViewProps = {
  product: Product;
  showModal: boolean;
  config?: string;
  modalTitle?: string;
  onClose: () => void;
  originator: "QUICK_VIEW_ICON" | "VIEW_OFFERS" | "ADD_TO_BASKET" | undefined;
};

export function QuickViewModal({
  product,
  showModal,
  config = "standard",
  modalTitle = "",
  onClose,
  originator,
}: QuickViewProps) {
  const { variant } = useVariant();
  const experiencesRawString = useExperienceString();
  useEffect(function loadQuickViewScript() {
    if (
      !window.customElements.get("quick-view") &&
      !document.getElementById("jl-quick-view-script")
    ) {
      const script = document.createElement("script");
      script.src = "/quick-view/quick-view.iife.js";
      script.id = "jl-quick-view-script";
      document.head.appendChild(script);
    }
  }, []);

  useEffect(
    function subscribeToQuickViewExit() {
      window.addEventListener("jl.quick-view.exit", onClose);

      return function unsubscribeFromQuickViewExit() {
        window.removeEventListener("jl.quick-view.exit", onClose);
      };
    },
    [onClose],
  );

  const analyticsData = getAnalyticsData(product, originator);

  return (
    <Modal
      isOpen={showModal}
      footer={false}
      onClose={onClose}
      width="extra-large"
      height="fixed"
      title={modalTitle}
    >
      {showModal && (
        <quick-view
          data-testid="quick-view"
          config={config}
          pim-product-id={product.aliases.pimProductId}
          initial-variant-id={variant?.id}
          analytics-additional-info={JSON.stringify(analyticsData)}
          experiences-raw-string={experiencesRawString}
        ></quick-view>
      )}
    </Modal>
  );
}
